import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useGetWorkflowsQuery,
  useDeleteWorkflowMutation,
  useUpdateWorkflowStatusMutation,
} from "@/store/rest_api";
import WorkflowFormDialog from "@/components/pages/workflows/WorkflowFormDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { TRIGGER_LABELS } from "@/components/pages/workflows/WorkflowConstants";

export default function WorkflowsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = location.pathname.endsWith("/new");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [showCreateDialog, setShowCreateDialog] = useState(isNew);

  const { data, isLoading } = useGetWorkflowsQuery({ page, size: 20, q: search || undefined });
  const [deleteWorkflow] = useDeleteWorkflowMutation();
  const [updateStatus] = useUpdateWorkflowStatusMutation();

  const workflows = data?.items ?? [];

  const handleDelete = async (workflowId: string) => {
    if (!confirm("Delete this workflow?")) return;
    try {
      await deleteWorkflow(workflowId).unwrap();
      toast.success("Workflow deleted");
    } catch {
      toast.error("Failed to delete workflow");
    }
  };

  const handleToggleStatus = async (workflowId: string, currentlyActive: boolean) => {
    try {
      await updateStatus({ id: workflowId, status: currentlyActive ? "INACTIVE" : "ACTIVE" }).unwrap();
      toast.success(currentlyActive ? "Workflow deactivated" : "Workflow activated");
    } catch {
      toast.error("Failed to update workflow status");
    }
  };

  return (
    <div className="space-y-6 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Workflows</h1>
          <p className="text-muted-foreground">Automate actions based on events</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-1" />
          New Workflow
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search workflows..."
          value={search}
          onChange={(event) => { setSearch(event.target.value); setPage(0); }}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-16 w-full" />
          ))}
        </div>
      ) : workflows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Zap className="h-10 w-10 mb-3 opacity-50" />
          <p className="text-sm">No workflows yet</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Create your first workflow
          </Button>
        </div>
      ) : (
        <div className="space-y-1">
          {workflows.map((workflow) => (
            <div
              key={workflow.id}
              className="flex items-center gap-3 px-4 py-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
              onClick={() => navigate(`/workflows/${workflow.id}`)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{workflow.name}</span>
                  <Badge variant="outline" className="text-xs">
                    {TRIGGER_LABELS[workflow.triggerType]}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    {workflow.actions.length} action{workflow.actions.length !== 1 ? "s" : ""}
                  </Badge>
                </div>
                {workflow.description && (
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{workflow.description}</p>
                )}
              </div>

              <Switch
                checked={workflow.status === "ACTIVE"}
                onCheckedChange={() => handleToggleStatus(workflow.id, workflow.status === "ACTIVE")}
                onClick={(event) => event.stopPropagation()}
              />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={(event) => event.stopPropagation()}>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={(event) => { event.stopPropagation(); navigate(`/workflows/${workflow.id}`); }}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive" onClick={(event) => { event.stopPropagation(); handleDelete(workflow.id); }}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {data.totalElements} workflow{data.totalElements !== 1 ? "s" : ""}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={data.last} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      )}

      <WorkflowFormDialog
        open={showCreateDialog}
        onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open && isNew) navigate("/workflows", { replace: true });
        }}
      />
    </div>
  );
}
